// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { spinnerStyle } from "@eisinc/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { CustomUpdateAccountNamesActions } from "@eisinc/App/Store/Reducers/UpdateAccountNamesReducer";
import { getAccountFirstName, getAccountLastName } from "@eisinc/App/Store/Selectors/CustomAccountSettingsSelectors";
import Button from "@eisinc/MobiusOverrides/Button";
import TextField, { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateAccountSettings from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/UpdateAccountSettings";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, FC } from "react";
import { connect, DispatchProp, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// <XNG-Changes>
interface OwnProps extends WidgetProps {}
// </XNG-Changes>

const mapDispatchToProps = {
    updateAccountSettings,
};

// XNG-Changes : use CustomAppState
const mapStateToProps = (state: CustomAppState) => ({
    editingAccount: state.pages.accountSettings.editingAccount,
    emailErrorMessage: state.pages.accountSettings.emailErrorMessage,
    useEmailAsUserName: getSettingsCollection(state).accountSettings.useEmailAsUserName,
    changePasswordUrl: getPageLinkByPageType(state, "ChangePasswordPage")?.url,
    // XNG-Changes
    firstName: getAccountFirstName(state),
    lastName: getAccountLastName(state),
});

// XNG-Changes use OwnProps instead of WidgetProps and add DispatchProp<CustomUpdateAccountNamesActions>
type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    DispatchProp<CustomUpdateAccountNamesActions>;
export interface AccountSettingsUserInformationStyles {
    userInformationTitle: TypographyProps;
    userInformationGridItem?: GridItemProps;
    changePasswordLink?: LinkPresentationProps;
    // XNG-Changes
    firstNameField?: TextFieldPresentationProps;
    userInformationWrapper?: InjectableCss;
    modalButtonsWrapper?: InjectableCss;
}

export const userInformationStyles: AccountSettingsUserInformationStyles = {
    userInformationGridItem: {
        width: 12,
    },
    changePasswordLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    userInformationTitle: {
        variant: "h2",
    },
};

const styles = userInformationStyles;
const password = "*************";

const AccountSettingsUserInformation: FC<Props> = ({
    editingAccount,
    useEmailAsUserName,
    changePasswordUrl,
    emailErrorMessage,
    updateAccountSettings,
    // XNG-Changes
    firstName,
    lastName,
}: Props) => {
    if (!editingAccount) {
        // XNG-Changes
        // return null;
        return (
            <StyledWrapper {...spinnerStyle.centeringWrapper}>
                <LoadingSpinner {...spinnerStyle.spinner} data-test-selector="myListsListSpinner"></LoadingSpinner>
            </StyledWrapper>
        );
    }

    // <XNG-Changes>
    const [isOpen, setOpenModal] = React.useState(false);
    const [firstLocal, setFirstLocal] = React.useState(firstName);
    const [lastLocal, setLastLocal] = React.useState(lastName);
    // </XNG-Changes>

    // XNG-Changes
    // const fullName = `${editingAccount.firstName} ${editingAccount.lastName}`;
    const fullName = `${firstName} ${lastName}`;
    const emailChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        updateAccountSettings({ email: event.currentTarget.value });
    };

    // <XNG-Changes>
    const closeUserNameModal = () => {
        setFirstLocal(firstName);
        setLastLocal(lastName);
        setOpenModal(false);
    };

    const continueInFullNameProcess = () => {
        updateAccountSettings({ firstName: firstLocal, lastName: lastLocal } as any);
        setOpenModal(false);
    };

    const firstNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFirstLocal(event.currentTarget.value);
    };

    const lastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLastLocal(event.currentTarget.value);
    };

    const resetValues = () => {
        setFirstLocal(firstName);
        setLastLocal(lastName);
        setOpenModal(true);
    };
    // </XNG-Changes>

    const label = (
        <>
            <Typography>{translate("Password")}</Typography>
            <Link {...styles.changePasswordLink} href={changePasswordUrl}>
                {translate("Change")}
            </Link>
        </>
    );

    // <XNG-Changes>

    const userLabel = (
        <>
            <Typography>{translate("Full Name")}</Typography>
            <Link {...styles.changePasswordLink} href="" onClick={resetValues}>
                {translate("Change")}
            </Link>
        </>
    );
    // </XNG-Changes>

    return (
        // XNG-Changes: use StyledWrapper
        <StyledWrapper
            css={css`
                margin-bottom: 5px;
            `}
        >
            <Typography {...styles.userInformationTitle}>{translate("Your Information")}</Typography>
            {/* XNG-Changes */}
            <GridContainer {...styles.userInformationWrapper}>
                {!useEmailAsUserName && (
                    <GridItem {...styles.userInformationGridItem}>
                        <TextField
                            label={translate("User Name")}
                            value={editingAccount.userName}
                            data-test-selector="accountSettings_userName"
                            disabled
                        />
                    </GridItem>
                )}
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        type="email"
                        label={translate("Email")}
                        onChange={emailChangeHandler}
                        value={editingAccount.email}
                        data-test-selector="accountSettings_email"
                        required
                        // maxLength={256}
                        error={emailErrorMessage}
                    />
                </GridItem>
                <GridItem {...styles.userInformationGridItem}>
                    {/* XNG-Changes: use userLabel */}
                    {/* <TextField label={translate("Full Name")} value={fullName} disabled /> */}
                    <TextField label={userLabel} value={fullName} disabled />
                </GridItem>

                {/* <XNG-Changes>: add Modal */}
                <Modal isOpen={isOpen} handleClose={closeUserNameModal} headline={translate("Change User Name")}>
                    <TextField
                        label={translate("First Name")}
                        value={firstLocal}
                        onChange={firstNameChangeHandler}
                        required
                        {...styles.firstNameField}
                        data-test-selector="accountSettings_firstName"
                    />
                    <TextField
                        label={translate("Last Name")}
                        value={lastLocal}
                        onChange={lastNameChangeHandler}
                        required
                        data-test-selector="accountSettings_lastName"
                    />
                    <StyledWrapper {...styles.modalButtonsWrapper}>
                        <Button onClick={continueInFullNameProcess}>{translate("Continue")}</Button>
                    </StyledWrapper>
                </Modal>
                {/* </XNG-Changes> */}

                <GridItem {...styles.userInformationGridItem}>
                    <TextField type="password" label={label} disabled value={password} />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsUserInformation),
    definition: {
        allowedContexts: ["AccountSettingsPage"],
        group: "Account Settings",
        displayName: "User Information",
    },
};

export default widgetModule;
